import { apiSlice } from './apiSlice';

export const payrollApi = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    getPayrollComponents: builder.query<any, Record<string, any> | void>({
      query: (params) => ({ url: '/payroll-components', params: params || {} }),
      providesTags: ['Payroll'],
    }),
    getPayrollComponentById: builder.query<any, number | string>({
      query: (id) => ({ url: `/payroll-components/${id}` }),
      providesTags: (result, error, id) => [{ type: 'Payroll', id }, 'Payroll'],
    }),
    createPayrollComponent: builder.mutation<any, Record<string, any>>({
      query: (data) => ({ url: '/payroll-components', method: 'POST', data }),
      invalidatesTags: ['Payroll'],
    }),
    updatePayrollComponent: builder.mutation<any, { id: number | string; data: Record<string, any> }>({
      query: ({ id, data }) => ({
        url: `/payroll-components/${id}`,
        method: 'PUT',
        data,
      }),
      invalidatesTags: (result, error, { id }) => [{ type: 'Payroll', id }, 'Payroll'],
    }),
    deletePayrollComponent: builder.mutation<any, number | string>({
      query: (id) => ({ url: `/payroll-components/${id}`, method: 'DELETE' }),
      invalidatesTags: ['Payroll'],
    }),
    
    // Employee salary setup — effective-dated component assignments.
    // Backend routes (HRM module): GET/POST /employees/{employee}/salary-setup,
    // PUT/DELETE /salary-setup/{setup}.
    getEmployeeSalarySetup: builder.query<any, { employeeId: number | string; params?: Record<string, any> }>({
      query: ({ employeeId, params }) => ({ url: `/employees/${employeeId}/salary-setup`, params }),
      providesTags: ['Payroll'],
    }),
    createEmployeeSalarySetup: builder.mutation<any, { employeeId: number | string; data: Record<string, any> }>({
      query: ({ employeeId, data }) => ({ url: `/employees/${employeeId}/salary-setup`, method: 'POST', data }),
      invalidatesTags: ['Payroll'],
    }),
    updateEmployeeSalarySetup: builder.mutation<any, { id: number | string; data: Record<string, any> }>({
      query: ({ id, data }) => ({ url: `/salary-setup/${id}`, method: 'PUT', data }),
      invalidatesTags: (result, error, { id }) => [{ type: 'Payroll', id }, 'Payroll'],
    }),
    deleteEmployeeSalarySetup: builder.mutation<any, number | string>({
      query: (id) => ({ url: `/salary-setup/${id}`, method: 'DELETE' }),
      invalidatesTags: ['Payroll'],
    }),

    getPayrollSettings: builder.query<any, Record<string, any> | void>({
      query: (params) => ({ url: '/payroll-settings', params: params || {} }),
      providesTags: ['Payroll'],
    }),
    updatePayrollSettings: builder.mutation<any, Record<string, any>>({
      query: (data) => ({ url: '/payroll-settings', method: 'PUT', data }),
      invalidatesTags: ['Payroll'],
    }),
    validatePayrollSettings: builder.mutation<any, void>({
      // Backend: GET payroll-settings/validation (validates GL account wiring).
      query: () => ({ url: '/payroll-settings/validation', method: 'GET' }),
    }),

    getPayrollPeriods: builder.query<any, Record<string, any> | void>({
      query: (params) => ({ url: '/payroll-periods', params: params || {} }),
      providesTags: ['Payroll'],
    }),
    getPayrollPeriodById: builder.query<any, number | string>({
      query: (id) => ({ url: `/payroll-periods/${id}` }),
      providesTags: (result, error, id) => [{ type: 'Payroll', id }, 'Payroll'],
    }),
    createPayrollPeriod: builder.mutation<any, Record<string, any>>({
      query: (data) => ({ url: '/payroll-periods', method: 'POST', data }),
      invalidatesTags: ['Payroll'],
    }),
    runPayrollPeriod: builder.mutation<any, number | string>({
      query: (id) => ({ url: `/payroll-periods/${id}/run`, method: 'POST' }),
      invalidatesTags: (result, error, id) => [{ type: 'Payroll', id }, 'Payroll'],
    }),
    closePayrollPeriod: builder.mutation<any, number | string>({
      query: (id) => ({ url: `/payroll-periods/${id}/close`, method: 'POST' }),
      invalidatesTags: (result, error, id) => [{ type: 'Payroll', id }, 'Payroll'],
    }),
    reopenPayrollPeriod: builder.mutation<any, number | string>({
      query: (id) => ({ url: `/payroll-periods/${id}/reopen`, method: 'POST' }),
      invalidatesTags: (result, error, id) => [{ type: 'Payroll', id }, 'Payroll'],
    }),
    recalculatePayrollPeriod: builder.mutation<any, number | string>({
      query: (id) => ({ url: `/payroll-periods/${id}/recalculate`, method: 'POST' }),
      invalidatesTags: (result, error, id) => [{ type: 'Payroll', id }, 'Payroll'],
    }),
    approvePayrollPeriod: builder.mutation<any, number | string>({
      query: (id) => ({ url: `/payroll-periods/${id}/approve`, method: 'POST' }),
      invalidatesTags: (result, error, id) => [{ type: 'Payroll', id }, 'Payroll'],
    }),
    reversePayrollPeriod: builder.mutation<any, number | string>({
      query: (id) => ({ url: `/payroll-periods/${id}/reverse`, method: 'POST' }),
      invalidatesTags: (result, error, id) => [{ type: 'Payroll', id }, 'Payroll'],
    }),
    disbursePayrollPeriod: builder.mutation<any, { id: number | string; bankAccountId?: number }>({
      query: ({ id, bankAccountId }) => ({ url: `/payroll-periods/${id}/disburse`, method: 'POST', data: { bankAccountId } }),
      invalidatesTags: (result, error, { id }) => [{ type: 'Payroll', id }, 'Payroll'],
    }),
    getPayrollPeriodSummary: builder.query<any, number | string>({
      query: (id) => ({ url: `/payroll-periods/${id}/summary` }),
      providesTags: ['Payroll'],
    }),
    getPeriodPayslips: builder.query<any, { periodId: number | string; params?: Record<string, any> }>({
      query: ({ periodId, params }) => ({ url: `/payroll-periods/${periodId}/payslips`, params }),
      providesTags: ['Payroll'],
    }),
    approvePayslip: builder.mutation<any, { periodId: number | string; payslipId: number | string }>({
      query: ({ periodId, payslipId }) => ({
        url: `/payroll-periods/${periodId}/payslips/${payslipId}/approve`,
        method: 'POST',
      }),
      invalidatesTags: ['Payroll'],
    }),
    approveAllPayslips: builder.mutation<any, number | string>({
      query: (periodId) => ({ url: `/payroll-periods/${periodId}/payslips/approve-all`, method: 'POST' }),
      invalidatesTags: ['Payroll'],
    }),
    // Payslip reads (mirrors the legacy payrollFoundationService URLs).
    getPayslip: builder.query<any, number | string>({
      query: (id) => ({ url: `/payslips/${id}` }),
      providesTags: (result, error, id) => [{ type: 'Payroll', id }, 'Payroll'],
    }),
    getPayslipComponents: builder.query<any, number | string>({
      query: (id) => ({ url: `/payslips/${id}/components` }),
      providesTags: (result, error, id) => [{ type: 'Payroll', id }, 'Payroll'],
    }),
    // F-3 — permission-gated vault reveal (payroll_sensitive_view required).
    revealPayslipSensitiveValues: builder.query<any, number | string>({
      query: (id) => ({ url: `/payslips/${id}/sensitive-values` }),
    }),
    // B-2 — multi-stage approval chain status for a period.
    getApprovalChain: builder.query<any, number | string>({
      query: (id) => ({ url: `/payroll-periods/${id}/approval-chain` }),
      providesTags: ['Payroll'],
    }),
    // F-2 — "My Approvals" inbox (pending stages for the current user).
    // Backend: GET payroll/approvals/pending (PayrollPeriodController@
    // pendingApprovals, payrollRunApprove permission). This used to point at
    // `/approvals/notifications`, duplicating approvalsApi's inbox endpoint
    // under a different tag — that URL is approval notifications, not the
    // payroll stages queue.
    getPendingApprovals: builder.query<any, void>({
      query: () => ({ url: '/payroll/approvals/pending' }),
      providesTags: ['Payroll'],
    }),
    
    getJournalPreview: builder.query<any, number | string>({
      query: (periodId) => ({ url: `/payroll-periods/${periodId}/journal-preview` }),
      providesTags: ['Payroll'],
    }),
    getDisbursementPreview: builder.query<any, number | string>({
      query: (periodId) => ({ url: `/payroll-periods/${periodId}/disbursement-preview` }),
      providesTags: ['Payroll'],
    }),
    getGlReconciliationDetail: builder.query<any, number | string>({
      query: (periodId) => ({ url: `/payroll-periods/${periodId}/gl-reconciliation/detail` }),
      providesTags: ['Payroll'],
    }),
    getGlReconciliationReport: builder.query<any, number | string>({
      query: (periodId) => ({ url: `/payroll-reports/gl-reconciliation/${periodId}` }),
      providesTags: ['Payroll'],
    }),
    getBankTransferFile: builder.query<any, { periodId: number | string; download?: boolean; formatCode?: string }>({
      query: ({ periodId, download, formatCode }) => {
        if (download) {
          const params: Record<string, any> = {};
          if (formatCode) params.format_code = formatCode;
          return { url: `/payroll-periods/${periodId}/bank-transfer-file`, params, responseType: 'blob' };
        }
        return { url: `/payroll-periods/${periodId}/bank-transfer-file`, method: 'POST', data: { format_code: formatCode } };
      },
    }),
    getPeriodExceptions: builder.query<any, number | string>({
      query: (periodId) => ({ url: `/payroll-periods/${periodId}/exceptions` }),
      providesTags: ['Payroll'],
    }),
    getPeriodAudit: builder.query<any, number | string>({
      query: (periodId) => ({ url: `/payroll-periods/${periodId}/audit` }),
      providesTags: ['Payroll'],
    }),
  }),
});

export const {
  useGetPayrollComponentsQuery,
  useGetPayrollComponentByIdQuery,
  useCreatePayrollComponentMutation,
  useUpdatePayrollComponentMutation,
  useDeletePayrollComponentMutation,
  useGetEmployeeSalarySetupQuery,
  useCreateEmployeeSalarySetupMutation,
  useUpdateEmployeeSalarySetupMutation,
  useDeleteEmployeeSalarySetupMutation,
  useGetPayrollSettingsQuery,
  useUpdatePayrollSettingsMutation,
  useValidatePayrollSettingsMutation,
  useGetPayrollPeriodsQuery,
  useGetPayrollPeriodByIdQuery,
  useCreatePayrollPeriodMutation,
  useRunPayrollPeriodMutation,
  useClosePayrollPeriodMutation,
  useReopenPayrollPeriodMutation,
  useRecalculatePayrollPeriodMutation,
  useApprovePayrollPeriodMutation,
  useReversePayrollPeriodMutation,
  useDisbursePayrollPeriodMutation,
  useGetPayrollPeriodSummaryQuery,
  useGetPeriodPayslipsQuery,
  useApprovePayslipMutation,
  useApproveAllPayslipsMutation,
  useRevealPayslipSensitiveValuesQuery,
  useLazyRevealPayslipSensitiveValuesQuery,
  useGetApprovalChainQuery,
  useGetPendingApprovalsQuery,
  useGetJournalPreviewQuery,
  useGetDisbursementPreviewQuery,
  useGetGlReconciliationDetailQuery,
  useGetGlReconciliationReportQuery,
  useGetBankTransferFileQuery,
  useGetPeriodExceptionsQuery,
  useGetPeriodAuditQuery,
} = payrollApi;
